"use client";

import Link from "next/link";
import { motion } from "framer-motion";
import { CheckCircle, Clock, BookOpen, ArrowRight, Lock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import type { Course, TrackGroup, Module } from "@/types";

interface TrackSelectionScreenProps {
  course: Course;
  trackGroup: TrackGroup;
  completedTrackModuleIds: string[];
  isAuthenticated: boolean;
}

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5,
      ease: "easeOut",
    },
  },
};

export function TrackSelectionScreen({
  course,
  trackGroup,
  completedTrackModuleIds,
  isAuthenticated,
}: TrackSelectionScreenProps) {
  const completedSet = new Set(completedTrackModuleIds);

  // Get all track modules for this track group
  const trackModules: Module[] = course.modules.filter((module) =>
    trackGroup.trackModuleIds.includes(module.id)
  );

  // Check if user has completed the required number of tracks
  const completedCount = completedTrackModuleIds.length;
  const hasMetRequirement = completedCount >= trackGroup.requiredTracksCount;

  return (
    <div className="bg-gradient-to-b from-secondary/30 via-background to-background">
      {/* Hero Section */}
      <section className="relative overflow-hidden border-b border-border/50 bg-gradient-to-br from-primary/5 via-background to-secondary/20 py-12 md:py-20">
        <div className="absolute inset-0 -z-10 overflow-hidden">
          <div className="absolute -right-20 top-0 h-96 w-96 rounded-full bg-primary/10 blur-3xl" />
          <div className="absolute -left-20 bottom-0 h-96 w-96 rounded-full bg-primary/10 blur-3xl" />
        </div>

        <motion.div
          initial="hidden"
          animate="visible"
          variants={containerVariants}
          className="container mx-auto px-4"
        >
          <div className="mx-auto max-w-4xl space-y-6 text-center">
            <Link
              href={`/courses/${course.slug}`}
              className="inline-flex items-center text-sm text-muted-foreground hover:text-primary"
            >
              ← Back to course overview
            </Link>

            <motion.div variants={itemVariants} className="space-y-4">
              <h1 className="text-3xl font-bold leading-tight text-foreground sm:text-4xl lg:text-5xl">
                {trackGroup.title}
              </h1>
              <p className="text-lg text-muted-foreground md:text-xl">
                {trackGroup.description}
              </p>

              {trackGroup.instructions && (
                <p className="text-sm text-muted-foreground">
                  {trackGroup.instructions}
                </p>
              )}
            </motion.div>

            <motion.div
              variants={itemVariants}
              className="flex flex-wrap items-center justify-center gap-3 text-sm"
            >
              <Badge variant={hasMetRequirement ? "default" : "secondary"} className="text-xs">
                {hasMetRequirement ? (
                  <>
                    <CheckCircle className="mr-1 h-3 w-3" />
                    Requirement met
                  </>
                ) : (
                  <>
                    Complete {trackGroup.requiredTracksCount} track
                    {trackGroup.requiredTracksCount > 1 ? "s" : ""} to continue
                  </>
                )}
              </Badge>

              <span className="inline-flex items-center gap-2 rounded-lg bg-secondary/80 px-4 py-2 font-medium text-secondary-foreground">
                <BookOpen className="h-4 w-4" />
                {trackModules.length} tracks available
              </span>

              <span className="inline-flex items-center gap-2 rounded-lg bg-secondary/80 px-4 py-2 font-medium text-secondary-foreground">
                Progress: {completedCount} / {trackModules.length}
              </span>
            </motion.div>
          </div>
        </motion.div>
      </section>

      {/* Track Cards */}
      <section className="container mx-auto px-4 py-12 md:py-16">
        <div className="mx-auto max-w-6xl">
          {!isAuthenticated && (
            <div className="mb-8 rounded-xl border border-border/60 bg-secondary/20 p-6 text-center">
              <p className="mb-4 text-sm text-muted-foreground">
                Sign in to track your progress and access all course features
              </p>
              <Button asChild>
                <Link href="/auth/login">Sign in</Link>
              </Button>
            </div>
          )}

          <motion.div
            initial="hidden"
            animate="visible"
            variants={containerVariants}
            className="grid gap-6 sm:grid-cols-2 lg:grid-cols-2"
          >
            {trackModules.map((trackModule, index) => {
              const isCompleted = completedSet.has(trackModule.id);
              const totalLessons = trackModule.lessons.length;
              const totalMinutes = trackModule.lessons.reduce(
                (sum, lesson) => sum + (lesson.durationMinutes || 0),
                0
              );

              const firstLesson = trackModule.lessons[0];

              return (
                <motion.div
                  key={trackModule.id}
                  variants={itemVariants}
                  whileHover={{ y: -4, transition: { duration: 0.2 } }}
                  className={`group relative overflow-hidden rounded-2xl border shadow-lg transition-shadow hover:shadow-xl ${
                    isCompleted
                      ? "border-success/50 bg-gradient-to-br from-success/5 to-background"
                      : "border-border/50 bg-gradient-to-br from-background to-secondary/20"
                  }`}
                >
                  <div className="p-6 md:p-8">
                    {/* Track Icon/Title */}
                    <div className="mb-4 flex items-start justify-between">
                      <div className="flex items-center gap-3">
                        {trackModule.trackIcon && (
                          <div className="text-4xl">{trackModule.trackIcon}</div>
                        )}
                        <div>
                          <h3 className="text-xl font-bold text-foreground md:text-2xl">
                            {trackModule.trackName || trackModule.title}
                          </h3>
                          {isCompleted && (
                            <Badge variant="default" className="mt-1 gap-1 text-xs">
                              <CheckCircle className="h-3 w-3" />
                              Completed
                            </Badge>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Track Description */}
                    <p className="mb-6 text-sm text-muted-foreground md:text-base">
                      {trackModule.trackDescription || trackModule.summary}
                    </p>

                    {/* Track Stats */}
                    <div className="mb-6 flex flex-wrap gap-3 text-sm text-muted-foreground">
                      <span className="inline-flex items-center gap-1">
                        <BookOpen className="h-4 w-4" />
                        {totalLessons} lesson{totalLessons !== 1 ? "s" : ""}
                      </span>
                      <span className="inline-flex items-center gap-1">
                        <Clock className="h-4 w-4" />
                        {totalMinutes} minutes
                      </span>
                    </div>

                    {/* Action Button */}
                    {isAuthenticated ? (
                      <Button
                        asChild
                        variant={isCompleted ? "outline" : "default"}
                        className="w-full gap-2"
                        size="lg"
                      >
                        {firstLesson ? (
                          <Link href={`/courses/${course.slug}/lessons/${firstLesson.slug}`}>
                            {isCompleted ? (
                              <>
                                <CheckCircle className="h-4 w-4" />
                                Review Track
                              </>
                            ) : (
                              <>
                                Start Track
                                <ArrowRight className="h-4 w-4" />
                              </>
                            )}
                          </Link>
                        ) : (
                          <span>No lessons available</span>
                        )}
                      </Button>
                    ) : (
                      <Button variant="outline" disabled className="w-full gap-2" size="lg">
                        <Lock className="h-4 w-4" />
                        Sign in to start
                      </Button>
                    )}
                  </div>

                  {/* Decorative gradient overlay */}
                  <div
                    className={`absolute inset-0 -z-10 opacity-50 transition-opacity group-hover:opacity-70 ${
                      isCompleted
                        ? "bg-gradient-to-br from-success/10 via-transparent to-transparent"
                        : "bg-gradient-to-br from-primary/10 via-transparent to-transparent"
                    }`}
                  />
                </motion.div>
              );
            })}
          </motion.div>

          {/* Continue Button (if requirement met) */}
          {hasMetRequirement && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              className="mt-12 text-center"
            >
              <div className="mb-4 rounded-xl border border-success/50 bg-success/5 p-6">
                <CheckCircle className="mx-auto mb-2 h-8 w-8 text-success" />
                <p className="text-lg font-semibold text-foreground">
                  You've completed the required tracks!
                </p>
                <p className="mt-1 text-sm text-muted-foreground">
                  You can continue with the course or explore more tracks
                </p>
              </div>

              <Button asChild size="lg" className="gap-2">
                <Link href={`/courses/${course.slug}`}>
                  Continue Course
                  <ArrowRight className="h-4 w-4" />
                </Link>
              </Button>
            </motion.div>
          )}
        </div>
      </section>
    </div>
  );
}
